<template>
  <div ref="container" class="import-map" aria-hidden="true"></div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import mapboxgl from 'mapbox-gl';
import { HiikerMapStyle } from '../../utils/models/MapStyles';
import { boundsOf, cameraPoints, type ImportMapModel } from '../../utils/import/importMapModel';

// The Import files preview: the app's standard map, not interactive. Every line is red and
// bold with a white casing, a left-out file's line is dashed (a hike from waypoints shows only
// its routed line, once planned), kept waypoints are the
// Waypoints map's own "other" marker, every other file fades while one is open, and the
// camera eases to the open file and back out when it closes. What to draw comes from
// importMapModel.ts; this only draws it.
const props = defineProps<{ model: ImportMapModel }>();

const MAPBOX_TOKEN = 'pk.eyJ1IjoiaGlpa2VyLW1hcHMiLCJhIjoiY2x0YThyZHppMWhmejJqbzExODBuY3dveiJ9.nBrtnXgbzxpAx_953HCB-w';
const PIN_ICON = 'custom_waypoint_icon_other';
const RED = '#DB2B35';
/** A lone waypoint or a very short line shouldn't zoom in to street level. */
const MAX_ZOOM = 15;
/** How long the camera takes to move to a file that's just been opened. */
const FOCUS_EASE_MS = 500;
const DIMMED_OPACITY = 0.45;

const container = ref<HTMLDivElement | null>(null);
let map: mapboxgl.Map | null = null;
let loaded = false;
let framedOn: string | null = null;
let resizeObserver: ResizeObserver | null = null;

const linesData = (m: ImportMapModel) => ({
  type: 'FeatureCollection',
  features: m.lines.map(l => ({
    type: 'Feature',
    properties: { dashed: l.dashed, opacity: l.dimmed ? DIMMED_OPACITY : 1 },
    geometry: { type: 'LineString', coordinates: l.points.map(p => [p.lon, p.lat]) }
  }))
});

const pinsData = (m: ImportMapModel) => ({
  type: 'FeatureCollection',
  features: m.pins.map(p => ({ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [p.lon, p.lat] } }))
});

function addLayers(m: mapboxgl.Map): void {
  m.addSource('import-lines', { type: 'geojson', data: linesData(props.model) as any });
  m.addSource('import-pins', { type: 'geojson', data: pinsData(props.model) as any });
  const opacity: any = ['get', 'opacity'];
  m.addLayer({
    id: 'import-lines-casing',
    type: 'line',
    source: 'import-lines',
    layout: { 'line-join': 'round', 'line-cap': 'round' },
    paint: { 'line-color': '#FFFFFF', 'line-width': ['case', ['get', 'dashed'], 6.5, 8], 'line-opacity': opacity }
  });
  m.addLayer({
    id: 'import-lines-solid',
    type: 'line',
    source: 'import-lines',
    filter: ['!', ['get', 'dashed']],
    layout: { 'line-join': 'round', 'line-cap': 'round' },
    paint: { 'line-color': RED, 'line-width': 5, 'line-opacity': opacity }
  });
  m.addLayer({
    id: 'import-lines-dashed',
    type: 'line',
    source: 'import-lines',
    filter: ['get', 'dashed'],
    layout: { 'line-join': 'round' },
    paint: { 'line-color': RED, 'line-width': 3.5, 'line-dasharray': [1.5, 1.2], 'line-opacity': opacity }
  });
  m.addLayer({
    id: 'import-pins',
    type: 'symbol',
    source: 'import-pins',
    layout: { 'icon-image': PIN_ICON, 'icon-size': 0.32, 'icon-allow-overlap': true }
  });
}

// The style's sprite carries the waypoint marker; if a style ever doesn't, draw a plain pin.
function addFallbackPin(m: mapboxgl.Map): void {
  const size = 40;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.fillStyle = '#FFFFFF';
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = RED;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2 - 5, 0, Math.PI * 2);
  ctx.fill();
  m.addImage(PIN_ICON, ctx.getImageData(0, 0, size, size), { pixelRatio: 0.8 });
}

/** Frames the open file, or everything — only when that set changes. The first frame jumps. */
function frame(): void {
  if (!map || !loaded) return;
  const points = cameraPoints(props.model);
  const bounds = boundsOf(points);
  if (!bounds) return;
  const key = JSON.stringify(bounds);
  if (key === framedOn) return;
  const first = framedOn === null;
  framedOn = key;
  map.fitBounds(bounds, { padding: 28, maxZoom: MAX_ZOOM, duration: first ? 0 : FOCUS_EASE_MS });
}

function render(): void {
  if (!map || !loaded) return;
  (map.getSource('import-lines') as mapboxgl.GeoJSONSource | undefined)?.setData(linesData(props.model) as any);
  (map.getSource('import-pins') as mapboxgl.GeoJSONSource | undefined)?.setData(pinsData(props.model) as any);
  frame();
}

onMounted(() => {
  if (!container.value) return;
  try {
    mapboxgl.accessToken = MAPBOX_TOKEN;
    map = new mapboxgl.Map({
      container: container.value,
      style: HiikerMapStyle.standard().styleUrl,
      interactive: false,
      attributionControl: false,
      center: [0, 20],
      zoom: 1
    });
    map.addControl(new mapboxgl.AttributionControl({ compact: true }));
    map.on('styleimagemissing', (e: any) => {
      if (e.id === PIN_ICON && map && !map.hasImage(PIN_ICON)) addFallbackPin(map);
    });
    map.on('load', () => {
      if (!map) return;
      loaded = true;
      addLayers(map);
      frame();
    });
    // The dialog lays out around the map (and changes shape on mobile widths).
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => map?.resize());
      resizeObserver.observe(container.value);
    }
  } catch (e) {
    // No WebGL: the dialog still works without its preview.
    console.warn('Import preview map unavailable', e);
  }
});

watch(() => props.model, render);

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  map?.remove();
  map = null;
});
</script>

<style scoped>
.import-map {
  width: 100%;
  height: 100%;
  min-height: 160px;
  border-radius: 12px;
  overflow: hidden;
  background: #2b3a2f;
  pointer-events: none;
}
</style>
